/**
 * Canvas resume section helpers — fuzzy matching, reorder, content sanitization.
 */

import type { UserProfile } from '../../../types/userProfile';
import type { ResumeData } from './CanvasProvider';
import { CANVAS_SECTION_TITLE_MAP, canvasSectionDisplayTitle } from '../agentSession';
import {
  bucketResumeSections,
  normalizeSectionTitles,
  parseResumeSections,
  sortSectionsByCanonicalOrder,
} from '../../dashboard/components/AIResumeOptimizer/utils/parseResumeSections';
import { sanitizeResumeLlmOutput } from '../resumeLayoutGuardrails';

export type CanvasSectionRow = { title: string; content: string };

/** Titles that may appear in the canvas for each canonical section key. */
const SECTION_TITLE_ALIASES: Record<string, string[]> = {
  experience: ['work experience', 'professional experience', 'employment history', 'experience'],
  summary: ['professional summary', 'summary', 'profile'],
  skills: ['technical skills', 'skills', 'core competencies', 'key skills'],
  projects: ['projects & achievements', 'projects and achievements', 'projects', 'project', 'achievements'],
  education: ['education', 'academic background', 'qualifications'],
  certifications: ['certifications', 'certificates', 'licenses'],
  references: ['references'],
  /** "achievements" lives under projects only — avoids Awards vs Projects mismatch */
  awards: ['awards', 'honors', 'honours', 'rewards'],
  research: ['research', 'research experience', 'publications'],
  languages: ['languages'],
  personal: ['personal information', 'contact information', 'contact details', 'contact', 'header'],
};

export function parseCanvasSectionKeyFromText(text: string): string | null {
  const t = text.toLowerCase();
  if (/\b(proejct|projec)\b/.test(t)) return 'projects';
  if (/\bprojects?\b/.test(t)) return 'projects';
  if (/\b(experiecne|experiance|experence)\b/.test(t)) return 'experience';
  if (/\b(educat|edcation|educaton|education|educational|degree|university|college)\b/.test(t)) return 'education';
  if (/\b(certif\w*|certificate|license)\b/.test(t)) return 'certifications';
  if (/\b(referenc)\b/.test(t)) return 'references';
  if (/\b(award|honou?r|reward)\b/.test(t)) return 'awards';
  if (/\b(research|publication)\b/.test(t)) return 'research';
  if (/\b(language|lingual)\b/.test(t)) return 'languages';
  if (/\b(contact|personal\s+info|personal\s+information|header)\b/.test(t)) return 'personal';
  for (const key of Object.keys(CANVAS_SECTION_TITLE_MAP)) {
    const title = CANVAS_SECTION_TITLE_MAP[key].toLowerCase();
    if (t.includes(key) || t.includes(title)) return key;
    const aliases = SECTION_TITLE_ALIASES[key] ?? [];
    if (aliases.some((a) => t.includes(a))) return key;
  }
  return null;
}

export function canonicalKeyForSectionTitle(title: string): string {
  return parseCanvasSectionKeyFromText(title) ?? title.toLowerCase().trim();
}

/** One row per canonical section key; keeps richest content; preserves first-seen order. */
export function dedupeCanvasSections(sections: CanvasSectionRow[]): CanvasSectionRow[] {
  const byKey = new Map<string, CanvasSectionRow>();
  const order: string[] = [];

  for (const row of sections) {
    const key = canonicalKeyForSectionTitle(row.title);
    if (!order.includes(key)) order.push(key);
    const prev = byKey.get(key);
    if (!prev || row.content.trim().length > prev.content.trim().length) {
      byKey.set(key, row);
    }
  }

  return order.map((key) => {
    const row = byKey.get(key)!;
    return {
      title: row.title,
      content: sanitizeSectionContentForCanvas(row.content, row.title),
    };
  });
}

/** User wants to delete a section from the canvas (not rewrite it). */
export function wantsRemoveSection(text: string): boolean {
  return (
    /\b(remove|delete|drop|clear|hide|strip|omit|exclude|take\s+out|get\s+rid\s+of|do\s+not\s+include|without)\b/i.test(text) &&
    /\b(section|projects?|achievements?|education|experience|skills?|awards?|rewards?|research|certif|references?|summary|languages?)\b/i.test(text)
  );
}

/** Section key to remove (e.g. "remove projects and achievements" → projects). */
export function parseRemoveSectionKeyFromText(text: string): string | null {
  if (!wantsRemoveSection(text)) return null;
  const t = text.toLowerCase();
  if (
    /projects?\s*(?:and|&)\s*achievements?|achievements?\s*(?:and|&)\s*projects?/i.test(t) ||
    /projects?\s*&\s*achievements?/i.test(t)
  ) {
    return 'projects';
  }
  return parseCanvasSectionKeyFromText(text);
}

/** Remove one section row by canonical key — content is discarded, never merged elsewhere. */
export function removeSectionByKey(
  sections: CanvasSectionRow[],
  key: string,
): { sections: CanvasSectionRow[]; removed: boolean; removedTitle?: string; message?: string } {
  const idx = findSectionIndexByKey(sections, key);
  if (idx < 0) {
    return {
      sections,
      removed: false,
      message: `There is no **${canvasSectionDisplayTitle(key)}** section on the canvas to remove.`,
    };
  }
  const removedTitle = sections[idx]!.title;
  const next = sections.filter((_, i) => i !== idx);
  return { sections: next, removed: true, removedTitle };
}

export function findSectionIndexByKey(
  sections: CanvasSectionRow[],
  key: string,
): number {
  const k = key.toLowerCase().trim();
  const canonical = (CANVAS_SECTION_TITLE_MAP[k] ?? canvasSectionDisplayTitle(k)).toLowerCase();

  const exact = sections.findIndex((s) => s.title.toLowerCase().trim() === canonical);
  if (exact >= 0) return exact;

  const needles = new Set<string>([
    k,
    canonical,
    ...(SECTION_TITLE_ALIASES[k] ?? []).map((a) => a.toLowerCase()),
  ].filter(Boolean));

  return sections.findIndex((s) => {
    const title = s.title.toLowerCase().trim();
    return [...needles].some((n) => title === n || title === `${n}s`);
  });
}

/** Standalone section-header fragments embedded mid-paragraph (PDF wrap artifacts). */
const ORPHAN_SECTION_HEADER_RE =
  /^(work\s+experience|professional\s+summary|technical\s+skills|projects?\s*(?:&|and)\s*achievements?|achievements?|awards?|honors?|references?|certifications?|education|experience|work|skills?|leadership|expertise)$/i;

/** Check if a line looks like a work experience bullet (not contact info). */
// Handle various Unicode bullet characters: • ● ○ ◦ ▪ ▸ ► ‣ ⁃ ∙ ⋅ ◾ ◽
const BULLET_CHARS_RE = /^[\u2022\u2023\u2043\u204C\u204D\u2219\u25AA\u25AB\u25B8\u25BA\u25CF\u25CB\u25E6\u2605\u2606\u2713\u2714\u2717\u2718\u27A2\u2981\u29BE\u29BF•●○◦▪▸►‣⁃∙⋅◾◽\-*]\s*/;

function isWorkExperienceBulletLine(line: string): boolean {
  const t = line.trim();
  // Must start with a bullet character
  if (!BULLET_CHARS_RE.test(t)) return false;
  // Contains action verbs typical of work experience
  return /\b(engineered|developed|led|managed|implemented|designed|built|created|delivered|achieved|drove|spearheaded|collaborated|established|improved|increased|reduced|launched|architected|integrated|optimized|automated|analyzed|executed|coordinated|directed|oversaw|mentored|trained|facilitated|streamlined|maintained|supported|resolved|deployed|migrated)\b/i.test(t);
}

/** Remove markdown heading markers and duplicate section headers from stored content. */
export function sanitizeSectionContentForCanvas(
  content: string,
  sectionTitle?: string,
): string {
  const cleaned = sanitizeResumeLlmOutput(content ?? '');
  const titleNorm = (sectionTitle ?? '').toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
  const isPersonalSection = /personal|contact|header/i.test(titleNorm);

  const lines = cleaned.split('\n').map((raw) => {
    let line = raw.trim();
    if (!line) return '';

    // For Personal Information sections, filter out work experience bullets
    if (isPersonalSection && isWorkExperienceBulletLine(line)) {
      return '';
    }

    const hashHeading = line.match(/^#{1,6}\s*(.*)$/);
    if (hashHeading) {
      const headingText = (hashHeading[1] ?? '').trim();
      if (!headingText) return '';
      const headingNorm = headingText.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
      if (titleNorm && (headingNorm === titleNorm || titleNorm.includes(headingNorm) || headingNorm.includes(titleNorm))) {
        return '';
      }
      return headingText;
    }

    if (/^#{1,6}$/.test(line)) return '';

    const lineNorm = line.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
    if (
      ORPHAN_SECTION_HEADER_RE.test(line) &&
      line.split(/\s+/).length <= 4 &&
      lineNorm !== titleNorm &&
      !(titleNorm && (titleNorm.includes(lineNorm) || lineNorm.includes(titleNorm)))
    ) {
      return '';
    }

    return line;
  });

  return normalizeListFormatting(
    lines
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim(),
  );
}

/** Split inline * / • bullets onto separate lines for canvas rendering. */
export function normalizeListFormatting(content: string): string {
  let c = (content ?? '').trim();
  if (!c) return c;
  c = c.replace(/\s+\*\s+/g, '\n• ');
  c = c.replace(/^\*\s+/gm, '• ');
  c = c.replace(/\s+(?=\d+\.\s)/g, '\n');
  return c.replace(/\n{3,}/g, '\n\n').trim();
}

/** User is asking a question — answer in chat, do not mutate canvas. */
export function detectConsultationIntent(text: string): boolean {
  const t = text.trim().toLowerCase();
  if (
    /\b(what (other|else)|which sections?|what sections? can|what can you add|what do you suggest|tell me what|list (the )?sections?|what sections? (are|do)|how many sections?)\b/i.test(t)
  ) {
    return true;
  }
  if (/\?/.test(t) && /\b(what|which|how|can you|could you|should i|do you)\b/i.test(t)) {
    if (/\b(section|add|include|resume|canvas)\b/i.test(t)) return true;
  }
  return false;
}

/** User wants canonical section ordering — not a content rewrite. */
export function wantsOrganizeSections(text: string): boolean {
  return (
    /\b(organis[ez]|reorganis[ez]|sort|arrange|order properly|proper order|properly|canonical order|fix order)\b/i.test(text) &&
    /\b(section|resume|order|layout)\b/i.test(text)
  );
}

/** Full-resume rewrite — not a single-section edit. */
export function wantsFullResumeRewrite(text: string): boolean {
  const t = text.trim();
  const sectionKey = parseCanvasSectionKeyFromText(t);
  const namesSingleSection =
    sectionKey &&
    /\b(rewrite|redo|write|improve|impriove|imprve|enhance|update|fix|refine|expand|add)\b/i.test(t) &&
    !/\b(all|entire|whole|full|every)\b/i.test(t);
  if (namesSingleSection) return false;

  if (/\b(rewrite all|redo all|re-?optimize|optimize again|from scratch|fresh start|start over|start again|start fresh)\b/i.test(t)) {
    return true;
  }
  if (/\b(rewrite|redo|regenerate)\b.{0,30}\b(entire|whole|full|all|everything)\b/i.test(t)) return true;
  if (/\b(write|generate|create|make)\b.{0,30}\b(entire|whole|full|all|everything)\b.{0,20}\b(resume|cv)\b/i.test(t)) {
    return true;
  }
  return false;
}

/** Drop mis-parsed or junk sections (fragment Research, work narrative in Awards). */
export function cleanupMisplacedCanvasSections(sections: CanvasSectionRow[]): CanvasSectionRow[] {
  let result = [...sections];

  // Remove fragmentary Research — orphaned continuation lines from PDF wrap / bad bucketing
  result = result.filter((s) => {
    const title = s.title.toLowerCase();
    if (!title.includes('research') && canonicalKeyForSectionTitle(s.title) !== 'research') return true;
    const content = s.content.trim();
    const lineCount = content.split('\n').filter((l) => l.trim()).length;
    if (content.length < 80) return false;
    if (lineCount <= 2 && /^to\s+[a-z]/i.test(content) && !/^[-•●▪◦*]\s/m.test(content)) return false;
    return true;
  });

  // Awards bucketed with work/project narrative — not real awards.
  // Detect common resume action verbs that indicate experience/project content.
  const awardsIdx = result.findIndex((s) => canonicalKeyForSectionTitle(s.title) === 'awards');
  if (awardsIdx >= 0) {
    const content = result[awardsIdx]!.content;
    const actionVerbRe = /\b(Spearheaded|Led the|Collaborated with|Developed|Engineered|Built|Implemented|Integrated|Designed|Managed|Responsible for|Owned|Delivered|Standardization|Deployed|Architected|Revamp|Campaign|Platform Launch|cross-functional|360-degree|Expansion of team|Controlling the|Product enhancement|Travel to|Gained deep|Total control|Managing Portfolio)\b/i;
    const bulletCount = (content.match(/^[•\-\*]\s/gm) || []).length;
    const looksLikeWork = actionVerbRe.test(content) || bulletCount >= 6;
    const looksLikeRealAwards =
      /\b(award|honou?r|recognition|finalist|scholar|excellence|won|received|prize|medal|distinction)\b/i.test(content) &&
      !/\*\*[A-Z][^*]{10,}\*\*:\s/i.test(content);
    if (looksLikeWork && !looksLikeRealAwards) {
      result = result.filter((_, i) => i !== awardsIdx);
    }
  }

  return result;
}

/** Collapse broken line-wrapped summary paragraphs into readable blocks. */
export function collapseSummaryLines(content: string): string {
  const lines = (content ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
  const bullets: string[] = [];
  const paras: string[] = [];
  let buf: string[] = [];
  const flush = () => {
    if (buf.length) paras.push(buf.join(' ').replace(/\s{2,}/g, ' '));
    buf = [];
  };
  for (const line of lines) {
    if (/^[-•●▪◦*]\s/.test(line)) {
      flush();
      bullets.push(line);
    } else {
      buf.push(line);
    }
  }
  flush();
  return [...paras, ...bullets].join('\n\n').trim();
}

/** Strip bracket placeholders before export. */
export function stripSectionPlaceholders(content: string): string {
  return (content ?? '')
    .replace(/\[\+\s*[^\]]+\]/g, '')
    .replace(/\[[^\]]{3,80}\]/g, '')
    .replace(/\*\*(Company Name|Job Title|Start Year|End Year[^\]]*)\*\*/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\s+\|\s*$/gm, '')
    .replace(/\|\s*\|/g, '|')
    .trim();
}

type ExperienceRow = {
  title?: string;
  company?: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  bullets?: string[];
};

/** Detect LLM output cut off mid-sentence (common when max_tokens is too low). */
export function isTruncatedResumeSection(content: string, sectionKey?: string): boolean {
  const t = (content ?? '').trim();
  if (t.length < 30) return true;
  if (/\b(with|and|for|to|in|of|the|a|an|or|by|at|,)\s*$/i.test(t)) return true;
  if (sectionKey === 'summary') {
    const sentences = t.split(/[.!?]+/).map((s) => s.trim()).filter((s) => s.length > 8);
    return sentences.length < 2;
  }
  if (sectionKey === 'skills' && /,\s*$/.test(t)) return true;
  if (sectionKey === 'experience' && !/\n\s*[•\-*]\s/m.test(t) && t.length > 80) return true;
  return false;
}

/** Replace placeholder tokens with real profile employers; strip any that remain. */
export function repairExperienceFromProfile(content: string, experiences: ExperienceRow[] | undefined): string {
  if (!content?.trim()) return content;
  let out = content;
  const exps = experiences ?? [];

  for (const exp of exps) {
    if (exp.company) {
      out = out.replace(/\[\+\s*Company Name\s*\]/gi, exp.company);
      out = out.replace(/\[Company Name\]/gi, exp.company);
    }
    if (exp.title) {
      out = out.replace(/\[\+\s*Job Title\s*\]/gi, exp.title);
      out = out.replace(/\[Job Title\]/gi, exp.title);
    }
    if (exp.startDate) {
      out = out.replace(/\[Start Date\]/gi, exp.startDate);
      out = out.replace(/\[Start Year\]/gi, exp.startDate);
    }
    if (exp.endDate) {
      out = out.replace(/\[End Date[^\]]*\]/gi, exp.endDate);
    }
  }

  // Repair "Unknown" company names — match against profile experience by title
  if (/\|\s*Unknown\b/i.test(out) && exps.length > 0) {
    const lines = out.split('\n');
    out = lines.map((line) => {
      const unknownMatch = line.match(/^(\*{0,2}[^|*]+\*{0,2})\s*\|\s*Unknown\b/i);
      if (!unknownMatch) return line;
      const titleRaw = unknownMatch[1].replace(/\*+/g, '').trim().toLowerCase();
      const matchedExp = exps.find((e) =>
        e.title && (titleRaw.includes(e.title.toLowerCase()) || e.title.toLowerCase().includes(titleRaw)),
      );
      if (matchedExp?.company) {
        return line.replace(/\|\s*Unknown\b/i, `| ${matchedExp.company}`);
      }
      // Try positional: use the Nth company from profile
      const roleLinesBefore = lines.slice(0, lines.indexOf(line)).filter((l) => /^\*{0,2}[A-Z]/.test(l)).length;
      const positionalExp = exps[roleLinesBefore];
      if (positionalExp?.company) {
        return line.replace(/\|\s*Unknown\b/i, `| ${positionalExp.company}`);
      }
      return line;
    }).join('\n');
  }

  // If still no company on a role line, inject from first profile employer
  const primary = exps.find((e) => e.company?.trim());
  if (primary?.company && /\|\s*(?:,\s*)?\|/.test(out)) {
    out = out.replace(/\|\s*,\s*/g, `| ${primary.company}, `);
  }
  if (primary?.company && /\|\s*\|/.test(out)) {
    out = out.replace(/\|\s*\|/g, `| ${primary.company} |`);
  }

  return stripSectionPlaceholders(out);
}

/** Headline aligned to the job the user is optimizing for (not stale profile tagline). */
export function buildTargetRoleHeadline(
  targetRole: string | undefined,
  resumeData: ResumeData | null | undefined,
): string {
  if (!targetRole?.trim()) return resumeData?.headline?.trim() || '';
  const roleShort = targetRole.split(/[,|(]/)[0]?.trim() || targetRole.trim();
  const recentTitle = resumeData?.experience?.[0]?.title?.trim();
  const base = recentTitle || resumeData?.headline?.split('|')[0]?.trim() || 'Product Leader';
  if (/design/i.test(roleShort)) {
    return `${roleShort} | Product Design & UX Strategy`;
  }
  if (/product/i.test(roleShort)) {
    return `${roleShort} | ${base}`;
  }
  return `${base} | Targeting ${roleShort}`;
}

/** Final pass: sanitize, repair placeholders, optionally fall back to original. */
export function finalizeResumeSectionContent(
  optimized: string,
  original: string,
  sectionKey: string,
  resumeData?: ResumeData | null,
  targetRole?: string,
): string {
  let out = sanitizeSectionContentForCanvas(optimized, sectionKey);

  if (sectionKey === 'experience') {
    out = repairExperienceFromProfile(out, resumeData?.experience);
    out = ensureExperienceEntrySpacing(out);
  } else {
    out = stripSectionPlaceholders(out);
  }

  const orig = (original ?? '').trim();
  if (
    orig.length > 40 &&
    (isTruncatedResumeSection(out, sectionKey) || (sectionContentHasPlaceholders(out) && sectionKey !== 'education'))
  ) {
    out = sanitizeSectionContentForCanvas(orig, sectionKey);
    if (sectionKey === 'experience') {
      out = repairExperienceFromProfile(out, resumeData?.experience);
      out = ensureExperienceEntrySpacing(out);
    }
  }

  if (sectionKey === 'summary' && targetRole && !isTruncatedResumeSection(out, sectionKey)) {
    const roleShort = targetRole.split(/[,|(]/)[0]?.trim();
    if (roleShort && !out.toLowerCase().includes(roleShort.toLowerCase().slice(0, 12))) {
      out = `${out.replace(/\s+$/, '')} Seeking to contribute as a ${roleShort}.`;
    }
  }

  // Remove truncated bullets (ending mid-word or with trailing "and", "the", etc.)
  if (sectionKey === 'experience' || sectionKey === 'projects') {
    out = removeTruncatedBullets(out);
  }

  return out.trim();
}

/**
 * Ensure blank lines between experience entries (role blocks).
 * Role headers start with **Title** | Company or similar bold patterns.
 */
function ensureExperienceEntrySpacing(content: string): string {
  const lines = content.split('\n');
  const result: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isRoleHeader = /^\*\*[A-Z]/.test(line.trim()) && /\|/.test(line);
    if (isRoleHeader && i > 0 && result.length > 0 && result[result.length - 1]!.trim() !== '') {
      result.push('');
    }
    result.push(line);
  }
  return result.join('\n');
}

/**
 * Remove bullets that are clearly truncated mid-sentence
 * (end without punctuation, end with "and", "the", "of", etc.)
 */
function removeTruncatedBullets(content: string): string {
  return content.split('\n').filter((line) => {
    const t = line.trim();
    if (!t.startsWith('•') && !t.startsWith('-') && !t.startsWith('*')) return true;
    const body = t.replace(/^[•\-*]\s*/, '').trim();
    if (!body || body.length < 5) return false;
    if (/\b(and|the|of|to|for|with|in|a|an)\s*$/i.test(body)) return false;
    return true;
  }).join('\n');
}

function formatExperienceFromResumeData(resumeData: ResumeData | null | undefined): string {
  const exps = resumeData?.experience ?? [];
  if (!exps.length) return '';
  return exps
    .map((exp) => {
      const dates = [exp.startDate, exp.endDate || 'Present'].filter(Boolean).join(' – ');
      const loc = exp.location ? ` | ${exp.location}` : '';
      const header = `**${exp.title}** | ${exp.company}${loc}${dates ? ` | ${dates}` : ''}`;
      const bullets = (exp.bullets ?? []).map((b) => `• ${b}`).join('\n');
      return bullets ? `${header}\n${bullets}` : header;
    })
    .join('\n\n');
}

function sectionNeedsProfileFallback(key: string, content: string): boolean {
  if (sectionContentHasPlaceholders(content)) return true;
  if (key === 'experience' && /\b(company name|job title|start year|\[add)\b/i.test(content)) return true;
  if (key === 'summary' && content.split(/\s+/).length < 12) return true;
  return false;
}

/**
 * Replace placeholder-heavy saved sections with structured profile data when available.
 */
export function enrichHydratedCanvasSections(
  sections: CanvasSectionRow[],
  profile: UserProfile | null | undefined,
  resumeData: ResumeData | null | undefined,
): CanvasSectionRow[] {
  return sections.map((row) => {
    const key = canonicalKeyForSectionTitle(row.title);
    let content = sanitizeSectionContentForCanvas(row.content, row.title);

    if (key === 'personal') {
      content = buildPersonalSectionContent(content, profile, resumeData, { enrichMissing: false });
    } else if (sectionNeedsProfileFallback(key, content)) {
      const seed = buildSectionSeedFromProfile(key, profile, resumeData);
      if (seed && !sectionContentHasPlaceholders(seed)) {
        content = sanitizeSectionContentForCanvas(seed, row.title);
      }
    }

    if (key === 'summary') {
      content = collapseSummaryLines(content);
    }

    return { title: row.title, content: stripSectionPlaceholders(content) || content };
  }).filter((s) => s.content.trim().length > 0);
}

/** Sanitize canvas sections for PDF/DOCX export. */
export function prepareSectionsForExport(sections: CanvasSectionRow[]): CanvasSectionRow[] {
  return sections.map((s) => {
    const key = canonicalKeyForSectionTitle(s.title);
    let content = stripSectionPlaceholders(sanitizeSectionContentForCanvas(s.content, s.title));
    if (key === 'summary') content = collapseSummaryLines(content);
    return { title: s.title, content };
  }).filter((s) => s.content.trim().length > 0);
}

/** Parse saved resume markdown into clean canvas rows (bucket + dedupe + sanitize). */
export function prepareHydratedCanvasSections(
  markdown: string,
  profile?: UserProfile | null,
  resumeData?: ResumeData | null,
): CanvasSectionRow[] {
  const parsed = parseResumeSections(markdown);
  const normalized = normalizeSectionTitles(parsed);
  const bucketed = bucketResumeSections(normalized);
  const sorted = sortSectionsByCanonicalOrder(bucketed);
  const base = cleanupMisplacedCanvasSections(
    dedupeCanvasSections(
      sorted
        .filter((s) => s.content.trim().length > 0)
        .map((s) => ({ title: s.title, content: s.content })),
    ),
  );
  if (profile || resumeData) {
    return enrichHydratedCanvasSections(base, profile, resumeData);
  }
  return base;
}

const PERSONAL_SECTION_KEYS = new Set([
  'personal',
  'contact',
  'contact information',
  'personal information',
  'personal details',
  'header',
]);

function isPersonalCanvasSection(title: string): boolean {
  const key = canonicalKeyForSectionTitle(title);
  return PERSONAL_SECTION_KEYS.has(key) || PERSONAL_SECTION_KEYS.has(title.toLowerCase().trim());
}

function stripJunkCanvasSections(sections: CanvasSectionRow[]): CanvasSectionRow[] {
  return sections.filter((s) => {
    const t = s.content.trim();
    if (!t) return false;
    if (/^[-•●▪◦*]\s*$/.test(t)) return false;
    return true;
  });
}

function ensurePersonalSectionFirst(
  sections: CanvasSectionRow[],
  profile?: UserProfile | null,
  resumeData?: ResumeData | null,
): CanvasSectionRow[] {
  const result = [...sections];
  const personalIdx = result.findIndex((s) => isPersonalCanvasSection(s.title));

  if (personalIdx >= 0) {
    // Found a Personal Information section — rebuild it from profile data to ensure
    // it doesn't contain work experience bullets or other misplaced content
    const existing = result[personalIdx];
    const rebuilt = buildPersonalSectionContent(
      existing.content,
      profile,
      resumeData,
      { enrichMissing: false },
    );
    result[personalIdx] = { title: existing.title, content: rebuilt };
    
    // Move to first position if not already there
    if (personalIdx > 0) {
      const [personal] = result.splice(personalIdx, 1);
      result.unshift(personal);
    }
    return result;
  }

  // No Personal Information section found — create one from profile/resumeData
  if (profile || resumeData) {
    const contact = buildPersonalSectionContent('', profile, resumeData, { enrichMissing: false });
    if (contact.trim()) {
      result.unshift({ title: 'Personal Information', content: contact });
    }
  }

  return result;
}

/**
 * Sanitize optimized section rows in place — no rebucketing.
 * Re-bucketing optimized LLM output corrupts boundaries and mixes summary/experience.
 */
export function applyOptimizedCanvasSections(
  sections: CanvasSectionRow[],
  profile?: UserProfile | null,
  resumeData?: ResumeData | null,
): CanvasSectionRow[] {
  if (!sections.length) {
    return ensurePersonalSectionFirst([], profile, resumeData);
  }

  const cleaned = dedupeCanvasSections(
    stripJunkCanvasSections(
      sections.map((s) => ({
        title: s.title,
        content: sanitizeSectionContentForCanvas(s.content, s.title),
      })),
    ),
  );

  return ensurePersonalSectionFirst(cleaned, profile, resumeData).filter(
    (s) => s.content.trim().length > 0,
  );
}

/**
 * Run agentic / raw canvas rows through the same bucket + cleanup pipeline as saved-resume hydrate,
 * and always lead with a profile-backed Personal Information block when available.
 */
export function finalizeAgenticCanvasSections(
  sections: CanvasSectionRow[],
  profile?: UserProfile | null,
  resumeData?: ResumeData | null,
): CanvasSectionRow[] {
  if (!sections.length) {
    return ensurePersonalSectionFirst([], profile, resumeData);
  }

  const normalized = normalizeSectionTitles(
    sections.map((s) => ({
      title: s.title,
      content: s.content,
      preview: s.content.slice(0, 120),
    })),
  );
  const bucketed = bucketResumeSections(normalized);
  const sorted = sortSectionsByCanonicalOrder(bucketed);
  let base = cleanupMisplacedCanvasSections(
    dedupeCanvasSections(
      stripJunkCanvasSections(sorted.map((s) => ({ title: s.title, content: s.content }))),
    ),
  );

  if (profile || resumeData) {
    base = enrichHydratedCanvasSections(base, profile, resumeData);
  }

  return ensurePersonalSectionFirst(base, profile, resumeData).filter((s) => s.content.trim().length > 0);
}

/** Normalize common placement typos so anchor detection still works. */
function normalizePlacementText(instr: string): string {
  return instr
    .toLowerCase()
    .replace(/\bexperiecne\b/g, 'experience')
    .replace(/\bexperiance\b/g, 'experience')
    .replace(/\bexperence\b/g, 'experience')
    .replace(/\beducaiton\b/g, 'education')
    .replace(/\beducaton\b/g, 'education')
    .replace(/\bedcation\b/g, 'education')
    .replace(/\bcertificatiosn\b/g, 'certifications')
    .replace(/\bcertificatoin\b/g, 'certifications')
    .replace(/\bimpriove\b/g, 'improve')
    .replace(/\bimprve\b/g, 'improve');
}

export type SectionPlacement = 'before' | 'after';

export type ParsedSectionPlacement = {
  moveKey: string;
  anchorKey: string;
  placement: SectionPlacement;
};

/** Parse "move X below Y" OR "Projects & Achievements move below experience". */
export function parsePlacementFromUserText(text: string): ParsedSectionPlacement | null {
  const raw = text.trim();
  const t = normalizePlacementText(raw);

  const placementWord = (w: string): SectionPlacement | null => {
    if (/\b(above|before|over)\b/.test(w)) return 'before';
    if (/\b(below|after|under|beneath)\b/.test(w)) return 'after';
    return null;
  };

  let m = t.match(
    /\b(?:move|reorder|relocate|shift|put|place|position|add|insert|include)\s+(?:the\s+)?(.+?)\s+(above|below|before|after|under|over|beneath)\s+(?:the\s+)?(.+?)(?:\s+section)?\s*$/i,
  );
  if (m?.[1] && m?.[3]) {
    const placement = placementWord(m[2]) ?? 'after';
    const moveKey = parseCanvasSectionKeyFromText(m[1].trim());
    const anchorKey = parseCanvasSectionKeyFromText(m[3].trim());
    if (moveKey && anchorKey) return { moveKey, anchorKey, placement };
  }

  m = raw.match(
    /^(.+?)\s+move\s+(above|below|before|after|under|over|beneath)\s+(?:the\s+)?(.+?)(?:\s+section)?\s*$/i,
  );
  if (m?.[1] && m?.[3]) {
    const placement = placementWord(m[2]) ?? 'after';
    const moveKey = parseCanvasSectionKeyFromText(m[1].trim());
    const anchorKey = parseCanvasSectionKeyFromText(m[3].trim());
    if (moveKey && anchorKey) return { moveKey, anchorKey, placement };
  }

  return null;
}

/** Section the user wants to move (e.g. "move skills above …" → skills). */
export function parseMovedSectionKeyFromText(text: string): string | null {
  const placement = parsePlacementFromUserText(text);
  if (placement) return placement.moveKey;
  const t = text.trim().toLowerCase();
  const m = t.match(
    /\b(?:move|reorder|relocate|shift|put|place|position)\s+(?:the\s+)?([a-z][a-z0-9\s&]{0,48}?)\s+(?:above|below|before|after|under|over|beneath)\b/i,
  );
  if (m?.[1]) {
    const key = parseCanvasSectionKeyFromText(m[1].trim());
    if (key) return key;
  }
  return null;
}

/** Strict insert — never appends to end unless anchor missing. */
export function insertSectionRelative(
  sections: CanvasSectionRow[],
  moveKey: string,
  anchorKey: string,
  placement: SectionPlacement,
): { sections: CanvasSectionRow[]; moved: boolean; message?: string } {
  const idx = findSectionIndexByKey(sections, moveKey);
  if (idx < 0) {
    return {
      sections,
      moved: false,
      message: `Could not find **${canvasSectionDisplayTitle(moveKey)}** to move.`,
    };
  }

  let anchorIdx = findSectionIndexByKey(sections, anchorKey);
  if (anchorIdx < 0) {
    return {
      sections,
      moved: false,
      message: `Could not find **${canvasSectionDisplayTitle(anchorKey)}** as anchor.`,
    };
  }

  const next = [...sections];
  const [movedRow] = next.splice(idx, 1);
  anchorIdx = findSectionIndexByKey(next, anchorKey);
  if (anchorIdx < 0) {
    return { sections, moved: false, message: 'Anchor section disappeared after remove.' };
  }

  const targetIdx = placement === 'before' ? anchorIdx : anchorIdx + 1;
  next.splice(targetIdx, 0, movedRow);

  return {
    sections: dedupeCanvasSections(next),
    moved: true,
  };
}

function anchorKeyFromInstruction(instr: string): string | null {
  const t = normalizePlacementText(instr);
  for (const key of Object.keys(CANVAS_SECTION_TITLE_MAP)) {
    const title = CANVAS_SECTION_TITLE_MAP[key].toLowerCase();
    const patterns = [
      new RegExp(`\\b(?:after|below|under|beneath)\\s+(?:the\\s+)?${key}\\b`, 'i'),
      new RegExp(`\\b(?:after|below|under|beneath)\\s+(?:the\\s+)?${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i'),
      new RegExp(`\\b(?:before|above)\\s+(?:the\\s+)?${key}\\b`, 'i'),
      new RegExp(`\\b(?:before|above)\\s+(?:the\\s+)?${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i'),
    ];
    if (patterns.some((p) => p.test(t))) return key;
    const aliases = SECTION_TITLE_ALIASES[key] ?? [];
    for (const alias of aliases) {
      if (new RegExp(`\\b(?:after|below|under|beneath)\\s+(?:the\\s+)?${alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(t)) {
        return key;
      }
      if (new RegExp(`\\b(?:before|above)\\s+(?:the\\s+)?${alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(t)) {
        return key;
      }
    }
  }
  return null;
}

/** Anchor section for placement (e.g. "above educaiton" → education). */
export function parseAnchorSectionKeyFromInstruction(
  instruction: string,
  sections: CanvasSectionRow[],
): string | null {
  const key = anchorKeyFromInstruction(instruction);
  if (key) return key;

  const t = normalizePlacementText(instruction);
  const m = t.match(
    /\b(?:above|below|under|after|before|beneath|over)\s+(?:the\s+)?([a-z][a-z0-9\s&]{1,40})(?:\s+section)?\b/i,
  );
  if (!m?.[1]) return null;

  const chunk = m[1].trim();
  const fromParser = parseCanvasSectionKeyFromText(chunk);
  if (fromParser) return fromParser;

  const idx = sections.findIndex((s) => {
    const title = s.title.toLowerCase();
    return title.includes(chunk) || chunk.split(/\s+/).every((w) => title.includes(w));
  });
  if (idx < 0) return null;
  return parseCanvasSectionKeyFromText(sections[idx].title);
}

export function applySectionReorder(
  sections: CanvasSectionRow[],
  moveKey: string,
  instruction: string,
): { sections: CanvasSectionRow[]; moved: boolean; message?: string } {
  const parsed = parsePlacementFromUserText(instruction);
  if (parsed) {
    const primary = insertSectionRelative(
      sections,
      parsed.moveKey,
      parsed.anchorKey,
      parsed.placement,
    );
    if (primary.moved) return primary;
    if (parsed.moveKey !== moveKey) {
      return insertSectionRelative(sections, moveKey, parsed.anchorKey, parsed.placement);
    }
    return primary;
  }

  const idx = findSectionIndexByKey(sections, moveKey);
  if (idx < 0) {
    return {
      sections,
      moved: false,
      message: `I couldn't find a **${canvasSectionDisplayTitle(moveKey)}** section yet. Say **"add ${moveKey} section"** and I'll create it, then place it where you want.`,
    };
  }

  const instrNorm = normalizePlacementText(instruction);
  const anchorKey = parseAnchorSectionKeyFromInstruction(instruction, sections);

  if (anchorKey) {
    const placement: SectionPlacement = /\b(before|above|over)\b/.test(instrNorm) ? 'before' : 'after';
    return insertSectionRelative(sections, moveKey, anchorKey, placement);
  }

  if (/\b(first|top|beginning|start)\b/.test(instrNorm)) {
    const next = [...sections];
    const [movedRow] = next.splice(idx, 1);
    const targetIdx = next[0]?.title.toLowerCase().includes('personal') ? 1 : 0;
    next.splice(targetIdx, 0, movedRow);
    return { sections: dedupeCanvasSections(next), moved: true };
  }

  if (/\b(last|end|bottom)\b/.test(instrNorm)) {
    const next = [...sections];
    const [movedRow] = next.splice(idx, 1);
    next.push(movedRow);
    return { sections: dedupeCanvasSections(next), moved: true };
  }

  return {
    sections,
    moved: false,
    message: `Could not tell where to place **${canvasSectionDisplayTitle(moveKey)}**. Try "move ${moveKey} below work experience".`,
  };
}

export function buildSectionSeedFromProfile(
  key: string,
  profile: UserProfile | null | undefined,
  resumeData: ResumeData | null | undefined,
): string {
  const src = resumeData;
  if (key === 'education') {
    const edu = src?.education?.length ? src.education : profile?.education;
    if (edu?.length) {
      return edu
        .map((e) => {
          const parts = [e.degree];
          if (e.institution) parts.push(`— ${e.institution}`);
          if (e.year) parts.push(`(${e.year})`);
          if (e.gpa) parts.push(`| GPA: ${e.gpa}`);
          return parts.filter(Boolean).join(' ');
        })
        .filter(Boolean)
        .join('\n');
    }
    return '[Degree] | [Institution] | [Year]\n[Add honors, coursework, or GPA if relevant]';
  }

  if (key === 'projects') {
    const projects = src?.projects;
    if (projects?.length) {
      return projects
        .map((p) => {
          const lines = [`**${p.name}**`];
          if (p.description) lines.push(`• ${p.description}`);
          if (p.technologies?.length) lines.push(`Technologies: ${p.technologies.join(', ')}`);
          return lines.join('\n');
        })
        .join('\n\n');
    }
    return '**[Project Name]**\n• [What you built and the impact]\n• [Technologies used]\n\n**[Second Project]**\n• [Outcome with metrics]';
  }

  if (key === 'certifications' && src?.certifications?.length) {
    return src.certifications.map((c) => `• ${c}`).join('\n');
  }

  if (key === 'experience') {
    const formatted = formatExperienceFromResumeData(src);
    if (formatted) return formatted;
  }

  if (key === 'summary') {
    const summary = src?.summary ?? profile?.professionalSummary;
    if (summary?.trim()) return collapseSummaryLines(summary.trim());
  }

  if (key === 'skills') {
    const skills = src?.skills?.length ? src.skills : profile?.skills;
    if (skills?.length) {
      const flat = skills.map((s) => (typeof s === 'string' ? s : String(s))).filter(Boolean);
      if (flat.length) return flat.map((s) => `• ${s}`).join('\n');
    }
  }

  if (key === 'personal') {
    return buildPersonalSectionContent('', profile, src, { enrichMissing: false });
  }

  return '';
}

export function canonicalSectionTitle(key: string): string {
  if (key === 'projects') return 'Projects & Achievements';
  if (key === 'personal') return 'Personal Information';
  return canvasSectionDisplayTitle(key);
}

export function extractPlacementInstruction(text: string): string | null {
  if (parsePlacementFromUserText(text)) return text.trim();
  if (
    /\b(below|under|beneath|after|before|above)\b/i.test(text) &&
    /\b(section|experience|experiecne|experiance|work|education|skills|projects?|summary|references|certif|awards?|technical)\b/i.test(text)
  ) {
    return text.trim();
  }
  return null;
}

export function sectionContentHasPlaceholders(content: string): boolean {
  return /\[[^\]]{3,80}\]/.test(content ?? '');
}

export function wantsDummyOrSampleFill(text: string): boolean {
  return /\b(dummy|dumy|sample|example|fill\s+in|placeholder|fabricat|made[\s-]?up)\b/i.test(text);
}

/** User wants to enrich existing content — not replace employers/roles from scratch. */
export function wantsPreserveAndExpandContent(text: string): boolean {
  return (
    /\b(add\s+more|more\s+detai\w*|additional\s+details?|extra\s+details?|expand|elaborate|flesh\s+out|enrich|beef\s+up|extend|go\s+deeper|bullet\s+points?|emphasize|emphasise|highlight|bold|make.*bold|key\s+points?|stand\s+out)\b/i.test(text) ||
    (/\b(enhance|improve|impriove|imprve|imprv\w*|strengthen)\b/i.test(text) && !/\b(rewrite|replace|from\s+scratch|start\s+over)\b/i.test(text))
  );
}

/** User wants bold/emphasis formatting on a specific section — not a full rewrite. */
export function wantsSectionFormattingEmphasis(text: string): boolean {
  const t = text.trim();
  if (!/\b(bold|emphasiz\w*|emphaz\w*|highlight|key\s+points?|stand\s+out)\b/i.test(t)) return false;
  return parseCanvasSectionKeyFromText(t) != null;
}

/** Apply **bold** markdown to category labels and key terms — preserves all text, no LLM. */
export function applyBoldEmphasisToSectionContent(content: string, sectionKey?: string): string {
  const lines = (content ?? '').split('\n');
  const out: string[] = [];

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (!trimmed) {
      out.push(raw);
      continue;
    }

    if (/^\*\*[^*]+\*\*\s*$/.test(trimmed)) {
      out.push(trimmed);
      continue;
    }

    let processed = trimmed;

    // "Category: items" or "• Category: items"
    const colonLine = processed.match(/^([-•●▪◦*]\s*)?([^*]{2,55}?):\s*(.+)$/);
    if (colonLine && !/\*\*/.test(colonLine[2]!)) {
      const prefix = colonLine[1] ?? '';
      processed = `${prefix}**${colonLine[2]!.trim()}:** ${colonLine[3]}`;
      out.push(processed);
      continue;
    }

    // Bullet — bold the leading phrase before comma/dash
    if (/^[-•●▪◦*]\s/.test(processed) && !/\*\*/.test(processed)) {
      const bulletPrefix = processed.match(/^([-•●▪◦*]\s)/)?.[1] ?? '• ';
      const body = processed.slice(bulletPrefix.length);
      const keyMatch = body.match(/^([^,–—|]{3,50}?)(\s*[,–—|]\s*.+)?$/);
      if (keyMatch?.[1]) {
        processed = `${bulletPrefix}**${keyMatch[1].trim()}**${keyMatch[2] ?? ''}`;
        out.push(processed);
        continue;
      }
    }

    // Skills — bold comma-separated groups
    if (sectionKey === 'skills' && processed.includes(',') && !processed.includes(':')) {
      processed = processed
        .split(',')
        .map((part) => {
          const p = part.trim();
          if (!p || /\*\*/.test(p)) return part;
          return ` **${p}**`;
        })
        .join(',')
        .trim();
      out.push(processed);
      continue;
    }

    // Skills plain line — bold leading token group before dash
    if (sectionKey === 'skills' && !/\*\*/.test(processed)) {
      const dashSplit = processed.match(/^([^–—-]{3,45}?)(\s*[–—-]\s*.+)?$/);
      if (dashSplit?.[1]) {
        processed = `**${dashSplit[1].trim()}**${dashSplit[2] ?? ''}`;
      }
    }

    out.push(processed);
  }

  return out.join('\n');
}

/** User said "yes" after assistant offered to bold/edit a section — not a full re-optimize. */
export function detectAffirmativeSectionEditConfirm(
  userMessage: string,
  messages: Array<{ role: string; content: string }>,
): { sectionKey: string } | null {
  if (!isAffirmativeShortReply(userMessage)) return null;

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  if (!lastAssistant) return null;

  const ac = lastAssistant.content;
  const askedEdit =
    /\b(bold formatting|add bold|emphasiz\w*|emphaz\w*|edit the content|would you like me to|key points|alternative ways to emphasize)\b/i.test(ac) &&
    /\b(section|skills?|experience|summary|technical|work experience|professional)\b/i.test(ac);

  if (!askedEdit) return null;

  const sectionKey =
    parseCanvasSectionKeyFromText(ac) ??
    inferSectionFromRecentMessages(messages, userMessage) ??
    parseCanvasSectionKeyFromText(messages.slice(-4).map((m) => m.content).join(' '));

  return sectionKey ? { sectionKey } : null;
}

/** Common chat typos: "no imprve" → "now improve". */
export function normalizeUserCommandTypos(text: string): string {
  return text
    .trim()
    .replace(/\bno\s+imprv\w*/gi, 'now improve')
    .replace(/\bimprv\w*/gi, 'improve')
    .replace(/\bimprive\b/gi, 'improve');
}

/** User said "no improve …" — not a request to edit (includes common typos like "no imprve"). */
export function isNegatedImproveIntent(text: string): boolean {
  return /\b(no|don't|do not|dont|not)\s+(improv\w*|imprv\w*|edit|update|change|add|enhance)\b/i.test(text);
}

/** Merge profile + canvas lines into a complete contact block (no LLM). */
/** Detect placeholder/empty-ish field values that should not appear on a resume. */
const JUNK_VALUE_RE = /^(not specified|not provided|n\/a|na|none|null|undefined|unknown|to be updated|tbd|your |enter )$/i;
function isUsableValue(v: string | undefined | null): v is string {
  if (!v) return false;
  const t = v.trim();
  return t.length > 0 && !JUNK_VALUE_RE.test(t);
}

export function buildPersonalSectionContent(
  existingContent: string,
  profile: UserProfile | null | undefined,
  resumeData: ResumeData | null | undefined,
  options: { enrichMissing?: boolean } = {},
): string {
  const enrichMissing = options.enrichMissing ?? true;
  const lines = (existingContent ?? '').split('\n').map((l) => l.trim()).filter(Boolean);

  let name = isUsableValue(profile?.displayName) ? profile!.displayName : (resumeData?.name ?? '');
  let headline = isUsableValue(profile?.headline) ? profile!.headline : (resumeData?.headline ?? '');
  let location = isUsableValue(profile?.location) ? profile!.location : (resumeData?.contact?.location ?? '');
  let email = resumeData?.contact?.email ?? '';
  let phone = resumeData?.contact?.phone ?? '';
  let linkedin = isUsableValue(profile?.linkedinUrl) ? profile!.linkedinUrl : (resumeData?.contact?.linkedin ?? '');
  let github = isUsableValue(profile?.githubUrl) ? profile!.githubUrl : (resumeData?.contact?.github ?? '');
  let portfolio = isUsableValue(profile?.portfolioUrl) ? profile!.portfolioUrl : (resumeData?.contact?.portfolio ?? '');

  // Skip lines that look like work experience bullets - they shouldn't be in Personal Info
  // Handle various Unicode bullet characters
  const isWorkExperienceBullet = (line: string): boolean => {
    const t = line.trim();
    // Starts with bullet and contains action verbs typical of work experience
    if (BULLET_CHARS_RE.test(t)) {
      return /\b(engineered|developed|led|managed|implemented|designed|built|created|delivered|achieved|drove|spearheaded|collaborated|established|improved|increased|reduced|launched|architected|integrated|optimized|automated|analyzed|executed|coordinated|directed|oversaw|mentored|defined|tracked)\b/i.test(t);
    }
    // Also check for long work descriptions without bullets
    if (t.length > 50 && /\b(engineered|developed|implemented|designed|built|created|delivered|achieved|spearheaded|collaborated|architected|integrated)\b/i.test(t)) {
      return true;
    }
    return false;
  };

  for (const line of lines) {
    // Skip work experience bullets that accidentally ended up in personal section
    if (isWorkExperienceBullet(line)) continue;
    
    const lower = line.toLowerCase();
    if (/@/.test(line) && !email) email = line.replace(/^[·|,\s]+/, '');
    else if (/linkedin/i.test(lower)) linkedin = line;
    else if (/github/i.test(lower)) github = line;
    else if (/\+?\d[\d\s\-()]{8,}/.test(line) && !phone) phone = line;
    else if (/^https?:\/\//i.test(line) && !portfolio) portfolio = line;
    else if (!location && line.length > 3 && line.length < 80 && !/@|linkedin|github|http/i.test(line)) {
      if (!headline || headline === name) headline = line;
      else if (!location) location = line;
    }
  }

  // Find first non-bullet line for name if not set from profile
  if (!name) {
    const firstValidLine = lines.find(l => !isWorkExperienceBullet(l) && !/@|linkedin|github|http|\+?\d{5,}/i.test(l));
    if (firstValidLine) name = firstValidLine;
  }
  if (!headline && lines[1] && !/@|linkedin|\+?\d/i.test(lines[1]) && !isWorkExperienceBullet(lines[1])) {
    headline = lines[1];
  }

  if (enrichMissing) {
    if (!phone) phone = '+91 98765 43210';
    if (!linkedin) linkedin = 'linkedin.com/in/your-profile';
    if (!email) email = 'your.email@example.com';
  }

  const out: string[] = [];
  const seen = new Set<string>();
  const pushUnique = (value: string) => {
    const v = value.trim();
    if (!v || JUNK_VALUE_RE.test(v)) return;
    const norm = v.toLowerCase().replace(/\s+/g, ' ');
    if (seen.has(norm)) return;
    if (norm === 'professional' && out.length > 1) return;
    seen.add(norm);
    out.push(v);
  };

  if (name) pushUnique(name);
  const headlineParts = [headline, profile?.currentRole].filter((v) => isUsableValue(v));
  const headlineLine = headlineParts.join(' | ');
  if (headlineLine && headlineLine !== name) pushUnique(headlineLine);
  if (email) pushUnique(email);
  if (phone) pushUnique(phone);
  if (location) pushUnique(location);
  if (linkedin) {
    pushUnique(
      linkedin.toLowerCase().includes('linkedin')
        ? linkedin
        : `linkedin.com/in/${linkedin.replace(/^https?:\/\//i, '')}`,
    );
  }
  if (github) pushUnique(github);
  if (portfolio) pushUnique(portfolio);

  return out.join('\n');
}

/** Employers parsed from "Company (2018-2020)" style experience lines. */
export function extractEmployerNamesFromExperience(content: string): string[] {
  const found = new Set<string>();
  const re = /(?:^|\n)\s*([A-Za-z0-9][A-Za-z0-9&.'\- ]{0,35}?)\s*\(\s*\d{4}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    const name = (m[1] ?? '').replace(/\*+/g, '').trim();
    if (name.length >= 2 && !/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|•)\b/i.test(name)) {
      found.add(name);
    }
  }
  return [...found];
}

export function employersMissingFromRewrite(original: string, updated: string): string[] {
  const employers = extractEmployerNamesFromExperience(original);
  if (employers.length === 0) return [];
  return employers.filter((e) => !updated.includes(e));
}

/** Upgrade classifier output when user text is explicit (placement, dummy fill, etc.). */
export function normalizeCanvasIntent<T extends { action: string; section: string | null; instruction: string; confidence: number }>(
  intent: T,
  userText: string,
  sections: CanvasSectionRow[],
): T {
  const t = userText.trim();
  if (detectConsultationIntent(t)) {
    return { ...intent, action: 'chat', section: null, instruction: t, confidence: 0.95 };
  }

  const placementParsed = parsePlacementFromUserText(t);
  const key = placementParsed?.moveKey ?? intent.section ?? parseCanvasSectionKeyFromText(t);
  const placement = extractPlacementInstruction(t);
  const exists = key ? findSectionIndexByKey(sections, key) >= 0 : false;

  if (placementParsed) {
    const wantsAdd = /\b(add|insert|create|include|new)\b/i.test(t);
    if (exists || !wantsAdd) {
      return {
        ...intent,
        action: 'move_section',
        section: placementParsed.moveKey,
        instruction: t,
        confidence: Math.max(intent.confidence, 0.92),
      };
    }
    return {
      ...intent,
      action: 'add_section',
      section: placementParsed.moveKey,
      instruction: t,
      confidence: Math.max(intent.confidence, 0.9),
    };
  }

  if (wantsOrganizeSections(t)) {
    return { ...intent, action: 'organize_sections', section: null, instruction: t, confidence: 0.95 };
  }

  if (key && placement) {
    const wantsAdd = /\b(add|insert|create|include|new)\b/i.test(t);
    if (exists && (wantsAdd || /\b(move|put|place|position|reorder)\b/i.test(t))) {
      return {
        ...intent,
        action: 'move_section',
        section: key,
        instruction: t,
        confidence: Math.max(intent.confidence, 0.9),
      };
    }
    if (!exists && wantsAdd) {
      return {
        ...intent,
        action: 'add_section',
        section: key,
        instruction: t,
        confidence: Math.max(intent.confidence, 0.88),
      };
    }
    if (exists) {
      return {
        ...intent,
        action: 'move_section',
        section: key,
        instruction: t,
        confidence: Math.max(intent.confidence, 0.85),
      };
    }
  }

  if (wantsPreserveAndExpandContent(t) && key) {
    return {
      ...intent,
      action: 'edit_section',
      section: key,
      instruction: t,
      confidence: Math.max(intent.confidence, 0.9),
    };
  }

  if (wantsDummyOrSampleFill(t) && key) {
    return {
      ...intent,
      action: 'edit_section',
      section: key,
      instruction: t,
      confidence: Math.max(intent.confidence, 0.9),
    };
  }

  if (/\b(enhance|improve|expand|complete|fill out)\b/i.test(t) && key) {
    return {
      ...intent,
      action: 'edit_section',
      section: key,
      instruction: t,
      confidence: Math.max(intent.confidence, 0.82),
    };
  }

  return intent;
}

/** When the user names a section, never leave intent as generic chat. */
export function boostCanvasIntentFromUserText<T extends { action: string; section: string | null; instruction: string; confidence: number }>(
  intent: T,
  userText: string,
  sections: CanvasSectionRow[],
): T {
  const t = userText.trim();

  if (detectConsultationIntent(t)) {
    return { ...intent, action: 'chat', section: null, instruction: t, confidence: 0.95 };
  }

  if (wantsOrganizeSections(t)) {
    return { ...intent, action: 'organize_sections', section: null, instruction: t, confidence: 0.95 };
  }

  const placementParsed = parsePlacementFromUserText(t);
  const key = placementParsed?.moveKey ?? parseCanvasSectionKeyFromText(t);

  if (wantsRemoveSection(t)) {
    const removeKey = parseRemoveSectionKeyFromText(t) ?? parseCanvasSectionKeyFromText(t);
    if (removeKey) {
      return {
        ...intent,
        action: 'remove_section',
        section: removeKey,
        instruction: t,
        confidence: 0.94,
      };
    }
  }

  if (placementParsed) {
    const exists = findSectionIndexByKey(sections, placementParsed.moveKey) >= 0;
    const wantsAdd = /\b(add|insert|create|include|new)\b/i.test(t);
    return {
      ...intent,
      action: exists || !wantsAdd ? 'move_section' : 'add_section',
      section: placementParsed.moveKey,
      instruction: t,
      confidence: 0.92,
    };
  }

  if (!key) return intent;

  const needsBoost =
    intent.action === 'chat' ||
    intent.confidence < 0.55 ||
    (intent.action === 'edit_section' && intent.section !== key);

  if (!needsBoost) return intent;

  if (extractPlacementInstruction(t)) {
    return normalizeCanvasIntent(
      { ...intent, action: 'move_section', section: key, instruction: t, confidence: 0.92 },
      t,
      sections,
    );
  }

  if (wantsPreserveAndExpandContent(t)) {
    return {
      ...intent,
      action: 'edit_section',
      section: key,
      instruction: t,
      confidence: 0.92,
    };
  }

  if (wantsDummyOrSampleFill(t) || /\b(enhance|improve|expand|details|fill|add\s+value|write)\b/i.test(t)) {
    return {
      ...intent,
      action: 'edit_section',
      section: key,
      instruction: t,
      confidence: 0.92,
    };
  }

  if (/\b(move|reorder|relocate|shift|put|place|position)\b/i.test(t)) {
    return {
      ...intent,
      action: 'move_section',
      section: key,
      instruction: t,
      confidence: 0.9,
    };
  }

  if (/\b(add|insert|create|include|new)\b/i.test(t)) {
    const exists = findSectionIndexByKey(sections, key) >= 0;
    if (exists && extractPlacementInstruction(t)) {
      return normalizeCanvasIntent(
        { ...intent, action: 'move_section', section: key, instruction: t, confidence: 0.9 },
        t,
        sections,
      );
    }
    return {
      ...intent,
      action: exists ? 'edit_section' : 'add_section',
      section: key,
      instruction: t,
      confidence: 0.88,
    };
  }

  return intent;
}

/** Short affirmative replies (yes, ok, sure, …). */
export function isAffirmativeShortReply(text: string): boolean {
  return /^\s*(ok|okay|yes|yeah|yep|yup|sure|go|proceed|confirm|do it|fine|great|sounds good|let'?s go|go ahead|start|begin|please do|do that|absolutely|definitely)\s*[!.]*\s*$/i.test(
    text.trim(),
  );
}

/** True when the assistant recently asked to re-run optimization for the same job. */
export function assistantAskedReoptimize(messages: Array<{ role: string; content: string }>): boolean {
  return messages.slice(-10).some(
    (m) =>
      m.role === 'assistant' &&
      /would you like to re-run the optimization|re-run the optimization with the latest|re-optimize fresh|optimize a fresh version|optimize again|want to start now|didn't optimize it yet/i.test(
        m.content,
      ),
  );
}

/** User said "yes" (etc.) to a pending re-optimization prompt — route to rewrite_all. */
export function detectReoptimizeConfirmIntent(
  userMessage: string,
  messages: Array<{ role: string; content: string }>,
): {
  action: 'rewrite_all';
  section: null;
  instruction: string;
  confidence: number;
} | null {
  if (!isAffirmativeShortReply(userMessage)) return null;
  if (!assistantAskedReoptimize(messages)) return null;
  return {
    action: 'rewrite_all',
    section: null,
    instruction: 'Re-run full resume optimization with latest profile data for the current canvas job',
    confidence: 0.96,
  };
}

export function inferSectionFromRecentMessages(
  messages: Array<{ role: string; content: string }>,
  userText: string,
): string | null {
  if (!isAffirmativeShortReply(userText) && !/^\s*(write\s+details?|add\s+details?|fill\s+(it\s+)?in|more\s+details?|expand|complete\s+it|go\s+ahead|do\s+it|please)\s*$/i.test(userText.trim())) {
    return null;
  }
  let lastSection: string | null = null;
  for (const m of messages.slice(-10)) {
    const k = parseCanvasSectionKeyFromText(m.content);
    if (k) lastSection = k;
  }
  return lastSection;
}

export function buildDummySectionContent(
  key: string,
  profile: UserProfile | null | undefined,
  resumeData: ResumeData | null | undefined,
): string | null {
  const roleHint = [
    profile?.currentRole,
    profile?.headline,
    resumeData?.headline,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const isFashion = /fashion|design|stylist|garment|textile|apparel/i.test(roleHint);

  if (key === 'education') {
    const seeded = buildSectionSeedFromProfile(key, profile, resumeData);
    if (seeded && !sectionContentHasPlaceholders(seeded)) return seeded;
    if (isFashion) {
      return [
        '**Bachelor of Design (Fashion Communication)**',
        'National Institute of Fashion Technology (NIFT), Mumbai | 2012 – 2016',
        '• Coursework: Illustration, Textile Science, Garment Construction, CAD for Fashion',
        '• Capstone: Sustainable capsule collection — 12 looks, NIFT graduate showcase',
        '',
        '**Diploma in Fashion Styling**',
        'Pearl Academy, Delhi | 2010 – 2012',
        '• Visual merchandising, trend forecasting, editorial styling',
      ].join('\n');
    }
    return [
      '**Bachelor of Science**',
      'University of Mumbai | 2014 – 2018',
      '• Relevant coursework: core degree subjects and capstone project',
    ].join('\n');
  }

  if (key === 'projects') {
    const seeded = buildSectionSeedFromProfile(key, profile, resumeData);
    if (seeded && !sectionContentHasPlaceholders(seeded)) return seeded;
    if (isFashion) {
      return [
        '**Sustainable Capsule Collection (2024)**',
        '• Led end-to-end design of 12-piece collection using organic cotton and natural dyes',
        '• Reduced sample waste by 30% through modular pattern blocks and digital fitting',
        'Tools: CLO 3D, Adobe Illustrator, Gerber AccuMark',
        '',
        '**Ethnic Wear E-commerce Launch (2023)**',
        '• Designed 40 SKUs for festive line; partnered with merchandising on sizing and MOQ',
        '• Contributed to 18% uplift in category revenue within first season',
      ].join('\n');
    }
    return [
      '**Customer Portal Redesign**',
      '• Rebuilt checkout flow; cut drop-off by 22% and improved mobile conversion',
      'Stack: React, TypeScript, Node.js',
    ].join('\n');
  }

  if (key === 'certifications') {
    if (isFashion) {
      return [
        '• CLO 3D Certification — CLO Virtual Fashion',
        '• Certified Fashion Designer — Fashion Institute accreditation',
        '• Sustainable Fashion Certification — Circular Fashion Alliance',
        '• Adobe Certified Professional — Illustrator & Photoshop',
      ].join('\n');
    }
    return [
      '• AWS Certified Solutions Architect — Associate',
      '• Google Professional Cloud Architect',
      '• Certified Scrum Master (CSM)',
      '• ISO 9001 Internal Auditor',
    ].join('\n');
  }

  if (key === 'references') {
    if (isFashion) {
      return [
        '**Elena Rossi** — Creative Director, Luxe Atelier | elena.rossi@luxe.com | +1 (212) 555-0142',
        '**Marcus Webb** — VP Design, Mode Executive | m.webb@mode.com | +1 (646) 555-0198',
      ].join('\n');
    }
    return [
      '**Sarah Chen** — Engineering Manager, TechCorp | sarah.chen@techcorp.com | +1 (415) 555-0134',
      '**David Okonkwo** — Director of Product, Acme Inc. | d.okonkwo@acme.com | +1 (650) 555-0177',
    ].join('\n');
  }

  if (key === 'awards') {
    if (isFashion) {
      return [
        '• Best Womenswear Collection — NIFT Graduate Showcase (2016)',
        '• Emerging Designer Award — Lakmé Fashion Week (2019)',
        '• Sustainability in Fashion — Circular Fashion Alliance (2022)',
        '• Top 40 Under 40 Designers — Vogue India (2023)',
      ].join('\n');
    }
    return [
      '• Employee of the Year — TechCorp (2022)',
      '• Innovation Excellence Award — Industry Summit (2023)',
      '• Top Performer — Regional Sales Leadership (2021)',
    ].join('\n');
  }

  if (key === 'personal') {
    return buildPersonalSectionContent('', profile, resumeData, { enrichMissing: true });
  }

  return null;
}
